'use client';
import { useEffect, useState } from 'react';
import ClaimIdentityButton from './ClaimIdentityButton';
import { formatEther, parseEther } from 'viem';
import { useSimulateShinkaiRegistryClaimIdentity } from '../generated';
import ApproveKaiButton from './ApproveKaiButton';
import { ZERO_ADDRESS, getStakeReq } from '../helper';
import { useDebounce } from 'usehooks-ts';
import { useGetIdentityData } from '../hooks/useGetIdentityData';
import { useGetUserKaiTokenBalance } from '../hooks/useGetUserKaiTokenBalance';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserKaiTokenAllowance } from '../hooks/useGetUserKaiTokenAllowance';
import { useGetShinkaiNamespaces } from '../hooks/useGetShinkaiNamespaces';
import { useGetDefaultShinkaiNamespace } from '../hooks/useGetDefaultShinkaiNamespace';
import { getShinkaiRegistryAddress } from '../contracts';
import { useEvmWallet } from '../hooks/useEvmWallet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { ChevronDown, AlertCircle } from 'lucide-react';

const inputClassName =
  'w-full bg-bg-secondary border border-divider rounded-lg px-3 py-2.5 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all';

const inputErrorClassName =
  'border-destructive/50 focus:ring-destructive/50 focus:border-destructive/50';

const labelClassName = 'text-sm font-medium text-text-secondary mb-1.5 block';

const selectClassName =
  'bg-bg-secondary border border-divider rounded-lg px-3 py-2.5 text-text-default text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all appearance-none cursor-pointer';

export default function ClaimIdentityForm() {
  const queryClient = useQueryClient();
  const queryParameters = new URLSearchParams(window.location.search);
  const referrerFromUrl = queryParameters.get('referrer');
  const encryptionPublicKeyFromUrl = queryParameters.get('encryption_pk') ?? '';
  const signaturePublicKeyFromUrl = queryParameters.get('signature_pk') ?? '';
  const nodeAddressFromUrl = queryParameters.get('node_address') ?? '';
  const { address } = useEvmWallet();
  const { data: shinkaiNamespaces } = useGetShinkaiNamespaces();
  const { data: defaultShinkaiNamespace } = useGetDefaultShinkaiNamespace();

  const referrerNamespaceFromUrl =
    referrerFromUrl?.substring(referrerFromUrl.lastIndexOf('.')) ?? '';
  const referrerNameFromUrl = referrerFromUrl?.substring(
    0,
    referrerFromUrl.lastIndexOf('.'),
  );

  const hasAdvancedDefaults =
    !!referrerFromUrl ||
    !!encryptionPublicKeyFromUrl ||
    !!signaturePublicKeyFromUrl ||
    !!nodeAddressFromUrl;

  const [identityName, setIdentityName] = useState('');
  const [identityNamespace, setIdentityNamespace] = useState(
    defaultShinkaiNamespace,
  );
  // biome-ignore lint/correctness/noUnusedVariables: setter kept for future referrer input feature
  const [referrerName, setReferrerName] = useState(referrerNameFromUrl ?? '');

  const [referrerNamespace, setReferrerNamespace] = useState(
    referrerNamespaceFromUrl
      ? referrerNamespaceFromUrl
      : defaultShinkaiNamespace,
  );

  const [stake, setStake] = useState('');
  const [stakeBN, setStakeBN] = useState(0n);
  const [claimIdentityError, setClaimIdentityError] = useState('');
  const [claimIdentitySettled, setClaimIdentitySettled] = useState(false);
  const [encryptionPublicKey, setEncryptionPublicKey] = useState(
    encryptionPublicKeyFromUrl,
  );
  const [signaturePublicKey, setSignaturePublicKey] = useState(
    signaturePublicKeyFromUrl,
  );
  const [nodeAddress, setNodeAddress] = useState(nodeAddressFromUrl);

  const debouncedReferrer = useDebounce(referrerName, 500);
  const { data: referrerIdentityData } = useGetIdentityData(
    debouncedReferrer + referrerNamespace,
  );

  const { data: shinTokenAllowance, queryKey: shinTokenAllowanceQueryKey } =
    useGetUserKaiTokenAllowance(getShinkaiRegistryAddress());

  const { data: shinTokenBalance } = useGetUserKaiTokenBalance();

  const debouncedName = useDebounce(identityName, 500);
  const referrerInvalid = !!debouncedReferrer && !referrerIdentityData;

  useEffect(() => {
    if (!referrerNamespace && referrerNamespaceFromUrl) {
      setReferrerNamespace(referrerNamespaceFromUrl);
    }
  }, [referrerNamespace, referrerNamespaceFromUrl]);

  const {
    refetch: refetchClaimIdentity,
    isSuccess: isSuccessClaimIdentity,
    error: errorClaimIdentity,
    isFetching,
  } = useSimulateShinkaiRegistryClaimIdentity({
    address: getShinkaiRegistryAddress(),
    args: [
      {
        name: debouncedName,
        namespace: shinkaiNamespaces[identityNamespace],
        stakeAmount: 0n,
        owner: ZERO_ADDRESS,
        referrer: !!debouncedReferrer
          ? debouncedReferrer + referrerNamespace
          : '',
      },
    ],
    query: {
      enabled:
        debouncedName.length > 0 &&
        !referrerInvalid &&
        shinkaiNamespaces != null,
    },
  });

  useEffect(() => {
    setIdentityNamespace(defaultShinkaiNamespace);
  }, [defaultShinkaiNamespace]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency on isFetching to reset settled state
  useEffect(() => {
    setClaimIdentitySettled(!!isSuccessClaimIdentity);
  }, [isSuccessClaimIdentity, isFetching]);

  useEffect(() => {
    if (errorClaimIdentity) {
      setClaimIdentityError(errorClaimIdentity.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorClaimIdentity]);

  const stakeReq = getStakeReq(
    identityName,
    identityNamespace,
    !!referrerIdentityData,
  );
  const nameUnavailable =
    claimIdentityError.includes('IdentityNotAvailable') ||
    identityName === 'localhost';
  const nameInvalid = new RegExp(/[^a-z0-9_]/).test(identityName);
  const stakeError = stakeBN < stakeReq;
  const balanceError = (shinTokenBalance ?? 0) < stakeBN;

  const errorsInInputs =
    stakeError || balanceError || referrerInvalid || nameInvalid;
  const buttonDisabled = errorsInInputs || !claimIdentitySettled;

  function handleNameInputChanged(newName: string) {
    setClaimIdentityError('');
    setClaimIdentitySettled(false);
    setIdentityName(newName);
    const newStakeReq = getStakeReq(
      newName,
      identityNamespace,
      !!referrerIdentityData,
    );
    handleStakeInputChanged(formatEther(newStakeReq));
  }

  function handleNamespaceInputChanged(newNamespace: string) {
    setClaimIdentityError('');
    setClaimIdentitySettled(false);
    setIdentityNamespace(newNamespace);
    const newStakeReq = getStakeReq(
      identityName,
      newNamespace,
      !!referrerIdentityData,
    );
    handleStakeInputChanged(formatEther(newStakeReq));
  }

  function handleStakeInputChanged(value: string) {
    let newValue = stake;
    try {
      setStakeBN(parseEther(value as `${number}`));
      newValue = value;
    } catch {
      // Invalid input, keep the old value
    }
    setStake(newValue);
  }

  function handleEncryptionPublicKeyInputChanged(newValue: string) {
    setEncryptionPublicKey(newValue);
  }

  function handleSignaturePublicKeyInputChanged(newValue: string) {
    setSignaturePublicKey(newValue);
  }

  function handleNodeAddressInputChanged(newValue: string) {
    setNodeAddress(newValue);
  }

  return (
    <div className="w-full max-w-xl mx-auto space-y-5">
      {/* Identity Name Input */}
      <div className="space-y-1.5">
        <span className={labelClassName}>Identity Name</span>
        <div className="flex gap-2">
          <label className="flex-1 relative">
            <span className="sr-only">Identity name</span>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
              @@
            </span>
            <input
              type="text"
              value={identityName}
              onChange={(e) => handleNameInputChanged(e.target.value)}
              placeholder="your_identity_name"
              className={cn(
                inputClassName,
                'pl-12',
                (nameUnavailable || nameInvalid) && inputErrorClassName,
              )}
            />
          </label>
          <label className="relative">
            <span className="sr-only">Namespace</span>
            <select
              value={identityNamespace}
              onChange={(e) => handleNamespaceInputChanged(e.target.value)}
              disabled={Object.keys(shinkaiNamespaces || {}).length <= 1}
              className={cn(
                selectClassName,
                'pr-8 min-w-[100px]',
                nameUnavailable && inputErrorClassName,
              )}
            >
              {Object.keys(shinkaiNamespaces || {}).map((namespaceString) => (
                <option key={namespaceString} value={namespaceString}>
                  {namespaceString}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
          </label>
        </div>
        {(nameUnavailable || nameInvalid) && (
          <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {nameUnavailable
              ? 'Identity is unavailable'
              : 'Can only contain lowercase letters, numbers, and underscores.'}
          </p>
        )}
      </div>

      {/* Stake Amount Input */}
      <label className="block space-y-1.5">
        <span className={labelClassName}>KAI Stake Amount</span>
        <input
          type="text"
          value={stake}
          onChange={(e) => handleStakeInputChanged(e.target.value)}
          placeholder="0.0"
          className={cn(
            inputClassName,
            (stakeError || balanceError) && inputErrorClassName,
          )}
        />
        {stakeError || balanceError ? (
          <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {balanceError
              ? 'Stake amount exceeds your KAI balance'
              : `Minimum stake requirement: ${formatEther(stakeReq)} KAI`}
          </p>
        ) : (
          <p className="text-xs text-text-tertiary mt-1">
            Minimum stake requirement: {formatEther(stakeReq)} KAI
          </p>
        )}
      </label>

      {/* Action Button */}
      <div className="pt-2">
        {(shinTokenAllowance ?? 0) < stakeBN ? (
          <ApproveKaiButton
            amount={stakeBN}
            callbackOnSuccess={() => {
              queryClient.invalidateQueries({
                queryKey: shinTokenAllowanceQueryKey,
              });
              refetchClaimIdentity();
            }}
            disabled={errorsInInputs}
          />
        ) : (
          <ClaimIdentityButton
            name={identityName}
            namespace={shinkaiNamespaces[identityNamespace]}
            namespaceString={identityNamespace}
            stakeAmount={stakeBN}
            owner={address ?? '0x0'}
            disabled={buttonDisabled || !address}
            referrer={
              !!debouncedReferrer ? debouncedReferrer + referrerNamespace : ''
            }
            loading={
              identityName.length > 0 &&
              !errorsInInputs &&
              !claimIdentitySettled &&
              !claimIdentityError
            }
            encryptionPublicKey={encryptionPublicKey.trim()}
            signaturePublicKey={signaturePublicKey.trim()}
            nodeAddress={nodeAddress.trim()}
          />
        )}
      </div>

      {/* Advanced Options */}
      <Accordion
        type="single"
        collapsible
        defaultValue={hasAdvancedDefaults ? 'advanced' : undefined}
        className="w-full"
      >
        <AccordionItem value="advanced" className="border-divider">
          <AccordionTrigger className="py-3 text-sm font-medium text-text-secondary hover:no-underline">
            Advanced Options
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              {/* Node Address */}
              <label className="block space-y-1.5">
                <span className={labelClassName}>Node Address</span>
                <input
                  type="text"
                  value={nodeAddress}
                  onChange={(e) =>
                    handleNodeAddressInputChanged(e.target.value)
                  }
                  placeholder="Optional node address"
                  className={inputClassName}
                />
              </label>

              {/* Public Keys Info */}
              <div className="rounded-lg bg-bg-tertiary/30 p-3 border border-divider">
                <p className="text-xs text-text-secondary">
                  You can supply your own public keys instead of having them
                  generated automatically.
                </p>
              </div>

              {/* Encryption Public Key */}
              <label className="block space-y-1.5">
                <span className={labelClassName}>Encryption Public Key</span>
                <input
                  type="text"
                  value={encryptionPublicKey}
                  onChange={(e) =>
                    handleEncryptionPublicKeyInputChanged(e.target.value)
                  }
                  placeholder="Optional encryption public key"
                  className={inputClassName}
                />
              </label>

              {/* Signature Public Key */}
              <label className="block space-y-1.5">
                <span className={labelClassName}>Signature Public Key</span>
                <input
                  type="text"
                  value={signaturePublicKey}
                  onChange={(e) =>
                    handleSignaturePublicKeyInputChanged(e.target.value)
                  }
                  placeholder="Optional signature public key"
                  className={inputClassName}
                />
              </label>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
